import { useCallback, useEffect, useState } from "react";
import {
  getPaymentSumsOverview,
  refreshRecentPaymentSums,
  refreshPaymentSumMonth,
} from "../../../../../services/clearing/payment-sums";

export type EroundStatus = "READY" | "GENERATING" | "FAILED" | "UNAVAILABLE";

export interface MonthlyPaymentSumRow {
  id: string;
  year: number;
  month: number;
  directPaySum: number;
  appPaySum: number;
  total: number;
  eroundSum: number | null;
  eroundStatus?: EroundStatus | null;
  lastUpdated: string;
  eroundLastUpdated: string | null;
}

export interface RefreshResult {
  rows: Omit<MonthlyPaymentSumRow, "id">[];
  failedMonths: { year: number; month: number }[];
}

const rowKey = (r: { year: number; month: number }) => `${r.year}_${r.month}`;

const withId = (r: Omit<MonthlyPaymentSumRow, "id">): MonthlyPaymentSumRow => ({
  ...r,
  id: rowKey(r),
});

const sortNewestFirst = (rows: MonthlyPaymentSumRow[]) =>
  [...rows].sort((a, b) => (b.year !== a.year ? b.year - a.year : b.month - a.month));

export const useMonthlyPaymentSums = () => {
  const [rows, setRows] = useState<MonthlyPaymentSumRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [rowRefreshing, setRowRefreshing] = useState<Record<string, boolean>>({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const data: Omit<MonthlyPaymentSumRow, "id">[] = await getPaymentSumsOverview();
      setRows(sortNewestFirst(data.map(withId)));
    } catch (e) {
      console.error("Failed to load payment sums overview", e);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const refreshRecent = useCallback(async (): Promise<RefreshResult | null> => {
    setRefreshing(true);
    try {
      const result: RefreshResult = await refreshRecentPaymentSums();
      setRows((prev) => {
        const byKey = new Map(prev.map((r) => [r.id, r]));
        result.rows.forEach((r) => {
          const row = withId(r);
          byKey.set(row.id, row);
        });
        return sortNewestFirst(Array.from(byKey.values()));
      });
      return result;
    } catch (e) {
      console.error("Failed to refresh recent payment sums", e);
      return null;
    } finally {
      setRefreshing(false);
    }
  }, []);

  const refreshMonth = useCallback(
    async (year: number, month: number): Promise<boolean> => {
      const key = `${year}_${month}`;
      setRowRefreshing((prev) => ({ ...prev, [key]: true }));
      try {
        const updated: Omit<MonthlyPaymentSumRow, "id"> = await refreshPaymentSumMonth({ year, month });
        const row = withId(updated);
        setRows((prev) => prev.map((r) => (r.id === key ? row : r)));
        return true;
      } catch (e) {
        console.error("Failed to refresh payment sum month", e);
        return false;
      } finally {
        setRowRefreshing((prev) => ({ ...prev, [key]: false }));
      }
    },
    [],
  );

  return { rows, loading, error, refreshing, rowRefreshing, refreshRecent, refreshMonth };
};
